/**
 * @fileOverview What the pointer does in the frame: pressing controls, typing into fields, picking from
 * menus, opening a thing with the right button, dragging one thing onto another. The catalog names
 * these as `ViewHelpAction`s; this runs them.
 *
 * Every action runs one of two ways. **Animated** is the story: the camera turns to the target, the
 * pointer travels there, rests, presses. **At speed** is a seek replaying earlier steps to put the page
 * into the state a later one is explained in — the same presses, with nothing to watch.
 */

import { i18n } from "@/plugins/i18n"
import { aimForControl, type Point } from "@/services/viewHelp/pointer"
import { isBoardOnToday } from "@/services/viewHelp/boardAnchors"
import { hoverOver, isLink } from "@/services/viewHelp/hover"
import {
  anchorRejection,
  findAnchors,
  isAnchorVisible,
  isScrolledDown,
  resolveScroller,
  scrollAnchorIntoView,
  scrollScrollerToTop,
  waitForVisible,
  waitForStableRect,
  type Rect,
} from "@/services/viewHelp/target"
import type { ViewHelpAction, ViewHelpText } from "@/services/viewHelp/types"
import {
  ACTION_TIMEOUT_MS,
  AFTER_ACTION_AT_SPEED_MS,
  AFTER_ACTION_MS,
  DRAG_HOLD_MS,
  HOVER_MS,
  LIFT_MS,
  HELD_KEY_MS,
  LINK_HOVER_MS,
  MAP_PAN_MS,
  PRESS_HOLD_MS,
  RIGHT_HOLD_MS,
  RIGHT_SHOW_MS,
  SCROLL_MS,
  SEARCH_SETTLE_MS,
  TYPE_MS,
  prefersReducedMotion,
  sleep,
} from "./timing"
import { Move, frameWindow, pointerCarry, pointerHolding, pointerKey, pressPointer, scaled } from "./state"
import { lookAt, lookAtRect, pullBackBeforeScrolling } from "./camera"
import { approach, flyPointerTo, restPointerOn } from "./pointer"
import { dispatchDrag, dispatchMouse, newDataTransfer, pressEscape, writeInput } from "./input"

type Targeted<Kind extends ViewHelpAction["do"]> = Extract<ViewHelpAction, { do: Kind }>

/**
 * Polls until `find` returns something, or the deadline passes — or the move it was started for is
 * overtaken, which answers null at once instead of waiting out a target that no longer matters.
 */
async function waitFor<T>(find: () => T | null | undefined, timeoutMs: number, move: Move): Promise<T | null> {
  const deadline = performance.now() + timeoutMs
  for (;;) {
    const found = find()
    if (found) return found
    if (performance.now() >= deadline || !move.current) return null
    await sleep(30)
  }
}

/**
 * True while a scene's exit runs. An exit's target is often gone for a good reason — Abbrechen on a
 * dialog that has already closed — so a miss there is not worth a warning.
 */
let quietMisses = false

/** Runs `actions` without warning about targets that are not there — for a scene's exit. */
export async function runActionsQuietly(actions: ViewHelpAction[], move: Move, animate: boolean) {
  quietMisses = true
  try {
    return await runActions(actions, move, animate)
  } finally {
    quietMisses = false
  }
}

/**
 * The element carrying an action's target id, once it has appeared — preferring a copy that is not
 * hidden or detached. A dialog opened again while its previous copy is still fading out has the same
 * ids twice, the leaving one first in the document: taken blindly, the press went to the copy on its way
 * out and was refused as hidden (the customer's assign dialog, on going back into its scene). Only when
 * no such copy turns up in time is the first one returned, so the refusal still says why.
 */
const waitForTarget = async (win: Window, id: string, timeoutMs: number, move: Move) => {
  const shown = await waitFor(
    () => findAnchors(win, id).find(el => !["hidden", "detached"].includes(anchorRejection(win, el) ?? "")) ?? null,
    timeoutMs,
    move
  )
  const found = shown ?? findAnchors(win, id)[0] ?? null
  // Said aloud: an action whose target never appeared used to fail in silence, and its step dropped off
  // the timeline with nothing in the console to say which press had nothing to press.
  if (!found && !quietMisses && move.current && import.meta.env.DEV) {
    console.warn(`[view-help] action target "${id}" not found on ${win.location.pathname}`)
  }
  return found
}

/** Time for the page to react after an action — a dialog to open, a list to filter. */
const settle = (move: Move, animate: boolean, floor = 0) =>
  move.pause(Math.max(animate ? scaled(AFTER_ACTION_MS) : AFTER_ACTION_AT_SPEED_MS, floor))

/** The text an action types or looks for, in the reader's language where it is a label. */
const textFor = (action: ViewHelpText) => (action.label ? i18n.global.t(action.label) : action.text)

/**
 * Where a press should land: the target itself when it is a control (a table row counts), otherwise
 * the control inside it. An anchor is often a wrapper — a column around a field, a Vuetify input around its control —
 * and a press on the wrapper does nothing. `.v-field` is the part of a Vuetify select or
 * autocomplete that opens its menu.
 */
function clickableIn(el: HTMLElement): HTMLElement {
  // A table row is its own control — the overview tables open a record on a click anywhere in the
  // row — and the buttons inside it (expand, a checkbox) are exactly what a press to open must miss.
  if (el.matches("button, a, input, textarea, tr, [role=button], [role=option]")) return el
  return (
    el.querySelector<HTMLElement>(
      "input[type=checkbox], input[type=radio], .v-field, button, a, input, textarea, [role=button]"
    ) ?? el
  )
}

/**
 * Where an open menu's options are. Vuetify's, and the personnel planning board's own — which is not
 * a Vuetify overlay at all but plain DOM teleported to the body, because it has to open at the mouse
 * rather than under a control. `choose` names an option by its text, and that is the same question in
 * both, so it asks it of both rather than of a menu kind the catalog would have to declare.
 */
const OPTION_SELECTOR = ".v-overlay-container .v-list-item, .context-menu .context-menu-item"

/** The first visible option of an open menu whose text contains `text`. */
function findOption(win: Window, text: string): HTMLElement | null {
  const wanted = text.toLowerCase()
  const options = Array.from(win.document.querySelectorAll<HTMLElement>(OPTION_SELECTOR))
  return (
    options.find(option => option.textContent?.toLowerCase().includes(wanted) && isAnchorVisible(win, option)) ?? null
  )
}

/**
 * Presses an element the way a mouse does: travel there, rest, press, release, click. The
 * `mousedown` is what opens a Vuetify select and what starts a control's own ripple; the `click` is
 * what runs its handler. Without `animate` it is only the press — a seek replaying earlier steps.
 */
async function pressElement(
  win: Window,
  el: HTMLElement,
  move: Move,
  animate: boolean,
  held?: "ctrl" | "shift"
): Promise<boolean> {
  const at = aimForControl(el.getBoundingClientRect())
  const mod = held === "ctrl" ? { ctrlKey: true } : held === "shift" ? { shiftKey: true } : {}
  if (animate) {
    // The key goes down before the pointer arrives, the way a hand does it, so the keycap beside the
    // arrow is read while it travels rather than flashing up for the length of one press.
    pointerKey.value = held ?? null
    if (!(await approach(at, move))) return false
    // Resting on it, it looks hovered — and a link rests a little longer, since pressing it leaves the
    // page, as does a press with a key held, whose keycap has to be read before it goes down.
    hoverOver(el)
    const rest = HOVER_MS + (isLink(el) ? LINK_HOVER_MS : 0) + (held ? HELD_KEY_MS : 0)
    if (!(await move.pause(scaled(rest)))) return false
    pressPointer()
  }
  dispatchMouse(win, el, "mousedown", at, mod)
  if (animate) await move.pause(scaled(PRESS_HOLD_MS))
  dispatchMouse(win, el, "mouseup", at, mod)
  // Overtaken while held down: let go without the click. The move that overtook this one may already
  // have tidied the page up — a scene's exit folding a row away — and a late click would undo that.
  if (!move.current) return false
  // `el.click()` carries no modifier, whatever was pressed: it is a method call, not an event the
  // page can read a `ctrlKey` off. So a held press is dispatched as a real event instead — which is
  // the whole reason the matrix's Strg- and Umschalt-Auswahl can be demonstrated at all.
  if (held) dispatchMouse(win, el, "click", at, mod)
  else el.click()
  return true
}

/**
 * Types into the first input inside `el`, a character at a time.
 *
 * The field is pressed first: an autocomplete opens its menu on a press, and a field that is being
 * typed into should look like it. The pointer then steps aside so it does not sit on the text.
 */
async function typeInto(win: Window, el: HTMLElement, text: string, move: Move, animate: boolean) {
  const field = el.matches("input, textarea")
    ? (el as HTMLInputElement)
    : el.querySelector<HTMLInputElement>("input:not([type=hidden]):not([type=checkbox]):not([type=radio]), textarea")
  if (!field) return false

  // A press on the field, but only its `mousedown` — which is what opens an autocomplete's menu. Not
  // a full click: the time picker opens its clock face on `mouseup`, and Vuetify's click-outside
  // ignores synthetic events, so every field typed into would leave its own clock face open, stacked
  // over the form.
  const at = aimForControl(field.getBoundingClientRect())
  if (animate) {
    if (!(await approach(at, move))) return false
    hoverOver(field)
    pressPointer()
  }
  dispatchMouse(win, field, "mousedown", at)

  if (!animate) {
    writeInput(win, field, text, text)
    return true
  }

  const box = field.getBoundingClientRect()
  await flyPointerTo({ x: box.right - 10, y: box.bottom + 12 }, move)
  for (let i = 1; i <= text.length; i++) {
    if (!move.current) return false
    writeInput(win, field, text.slice(0, i), text[i - 1])
    await move.pause(scaled(TYPE_MS + ((i * 37) % 5) * 9))
  }
  return move.current
}

// ── one function per kind of action ─────────────────────────────────────────

async function choose(win: Window, action: Targeted<"choose">, move: Move, animate: boolean, timeoutMs: number) {
  const option = await waitFor(() => findOption(win, textFor(action)), timeoutMs, move)
  if (!option || !move.current) return false
  lookAt(option, animate)
  if (!(await pressElement(win, option, move, animate))) return false
  await settle(move, animate)
  return move.current
}

/**
 * Lets go of a form field the walkthrough left the focus in.
 *
 * A Vuetify field keeps the focus after its menu is closed with Escape, and its ✕ puts the focus back
 * into it — both exactly what a person at the keyboard wants, and both wrong in a film: nobody is
 * typing in the frame, so a field still drawn as active reads as stuck. The filter step of the
 * time-tracking walkthrough left *Status* highlighted through every step after it.
 *
 * Only a field's own input. Escape also closes popovers, and the button that opened one gets its focus
 * back by design — that is not a field, and it is left alone.
 */
function releaseFieldFocus(win: Window) {
  // A cast rather than `instanceof`: the element belongs to the frame's realm, whose HTMLElement is
  // not this window's.
  const active = win.document.activeElement as HTMLElement | null
  if (active?.closest(".v-field")) active.blur()
}

/**
 * The ✕ of a clearable Vuetify field: an icon inside `.v-field__clearable`, which carries the click
 * handler. It is only shown while the field has a value, so finding none means the field is already
 * clear — which is what was asked for. No pointer and no menu: one press, and the value goes, which is
 * why exits undo a filter this way rather than by reopening the filter's menu.
 */
async function clear(win: Window, action: Targeted<"clear">, move: Move, animate: boolean) {
  const field = findAnchors(win, action.target)[0]
  const icon = field?.querySelector<HTMLElement>(".v-field__clearable .v-icon, .v-field__clearable i")
  if (icon && icon.offsetParent !== null) {
    icon.click()
    await settle(move, animate)
  }
  releaseFieldFocus(win)
  return move.current
}

/**
 * A dialog's Abbrechen, pressed like `click` — and not at all when it is not in the page. A scene's
 * exit uses it when the scene can end by saving: the form is gone by then, and waiting for its button
 * held the next step up for the whole action timeout.
 */
async function cancel(win: Window, action: Targeted<"cancel">, move: Move, animate: boolean, timeoutMs: number) {
  if (findAnchors(win, action.target).length === 0) return move.current
  return useControl(win, { do: "click", target: action.target }, move, animate, timeoutMs)
}

/**
 * Sets the page up for the actions after it. It never fails the step: a page too short to scroll is
 * already where the demonstration wants it.
 */
async function scrollPage(win: Window, action: Targeted<"scroll">, move: Move, animate: boolean, timeoutMs: number) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  const glide = animate && !prefersReducedMotion() ? scaled(SCROLL_MS) : 0
  const toTop = action.to === "top"

  if (animate) await pullBackBeforeScrolling(win, el, move, toTop ? isScrolledDown(win, el) : undefined)
  if (!move.current) return false
  if (toTop) await scrollScrollerToTop(win, el, glide, () => !move.current)
  else await scrollAnchorIntoView(win, el, glide, () => !move.current)
  if (!move.current) return false
  await waitForStableRect(win, el)
  return move.current
}

/**
 * Brings an action's target on screen and says whether it can be used. False — with the reason in
 * development — when it is there but not usable: scrolled out of its own scroller, hidden, too small.
 */
async function reveal(win: Window, id: string, el: HTMLElement, move: Move, animate: boolean): Promise<boolean> {
  const glide = animate && !prefersReducedMotion() ? scaled(SCROLL_MS) : 0
  if (animate) await pullBackBeforeScrolling(win, el, move)
  if (!move.current) return false
  await scrollAnchorIntoView(win, el, glide, () => !move.current)
  if (!move.current) return false
  if (!(await waitForVisible(win, el, undefined, () => !move.current))) {
    if (!move.current) return false
    if (import.meta.env.DEV) console.warn(`[view-help] action on "${id}" refused: ${anchorRejection(win, el)}`)
    return false
  }
  return true
}

/**
 * A tab pressed only when another one is open. A scene's `enter` presses the tab its dialog lives on,
 * so the scene can be reached from anywhere on the timeline — but played straight on from the step
 * that has just opened that tab, the same press again reads as a second demonstration of it.
 */
async function openTab(win: Window, action: Targeted<"openTab">, move: Move, animate: boolean, timeoutMs: number) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  const tab = el.closest<HTMLElement>("[role=tab]") ?? el.querySelector<HTMLElement>("[role=tab]")
  if (tab?.getAttribute("aria-selected") === "true") return move.current
  return useControl(win, { do: "click", target: action.target }, move, animate, timeoutMs)
}

/**
 * A planning board's Heute pressed only when the board does not already start on today
 * (`isBoardOnToday`). Every scene on the board presses it on the way in, so a jump from anywhere lands
 * on a board with bars in it — and played straight on from the scene before, which left the board on
 * today, the pointer walked up to Heute and pressed it for nothing, again and again.
 */
async function showToday(win: Window, action: Targeted<"showToday">, move: Move, animate: boolean, timeoutMs: number) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  if (isBoardOnToday(win.document)) return move.current
  return useControl(win, { do: "click", target: action.target }, move, animate, timeoutMs)
}

/**
 * A disclosure control pressed only when it is not already in the state asked for, read off its
 * `aria-expanded` (on the target or the control inside it). A plain `click` toggles: as a scene's
 * `exit` it would unfold a row whose step was overtaken before its own press — the reader moving on
 * while the pointer was still on its way — and leave it open over every step after.
 */
async function disclose(
  win: Window,
  action: Targeted<"expand" | "collapse">,
  move: Move,
  animate: boolean,
  timeoutMs: number
) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  const control = el.matches("[aria-expanded]") ? el : el.querySelector<HTMLElement>("[aria-expanded]")
  const open = control?.getAttribute("aria-expanded") === "true"
  if (open === (action.do === "expand")) return move.current
  return useControl(win, { do: "click", target: action.target }, move, animate, timeoutMs)
}

/**
 * Whether an on/off control is on: a checkbox or switch by its input's `checked`, a toggle button or
 * chip by `aria-pressed` / `aria-checked` — on the target or inside it. Null when it says neither.
 */
function isSwitchedOn(el: HTMLElement): boolean | null {
  const inputSelector = "input[type=checkbox], input[type=radio]"
  const input = el.matches(inputSelector) ? (el as HTMLInputElement) : el.querySelector<HTMLInputElement>(inputSelector)
  if (input) return input.checked
  const stateSelector = "[aria-pressed], [aria-checked]"
  const control = el.matches(stateSelector) ? el : el.querySelector<HTMLElement>(stateSelector)
  if (!control) return null
  return (control.getAttribute("aria-pressed") ?? control.getAttribute("aria-checked")) === "true"
}

/**
 * An on/off control pressed only when it is not already in the state asked for — the `expand` of
 * checkboxes, switches and toggle buttons. A control that says nothing about its state is pressed as a
 * plain click, with a warning in development: its anchor needs an `aria-pressed` to be undone safely.
 */
async function switchTo(
  win: Window,
  action: Targeted<"check" | "uncheck">,
  move: Move,
  animate: boolean,
  timeoutMs: number
) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  const on = isSwitchedOn(el)
  if (on === null && import.meta.env.DEV) {
    console.warn(`[view-help] "${action.target}" says nothing about being on or off — pressed as a click`)
  }
  if (on === (action.do === "check")) return move.current
  return useControl(win, { do: "click", target: action.target }, move, animate, timeoutMs)
}

/** A press on a control, or typing into a field: brought into view, looked at, then used. */
async function useControl(
  win: Window,
  action: Targeted<"click" | "type">,
  move: Move,
  animate: boolean,
  timeoutMs: number
) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  if (!(await reveal(win, action.target, el, move, animate))) return false

  lookAt(el, animate)
  const done =
    action.do === "type"
      ? await typeInto(win, el, textFor(action), move, animate)
      : await pressElement(win, clickableIn(el), move, animate, action.do === "click" ? action.with : undefined)
  if (!done) return false
  await settle(move, animate, action.do === "type" ? SEARCH_SETTLE_MS : 0)
  return move.current
}

/**
 * The pointer travels to a control and rests on it — see the `hover` action. Its hover look is the
 * player's copy of the page's `:hover` rules (`hoverOver`); the `mouseover` is for the pages that
 * listen for the event instead of styling the state — the matrix lights its column heading and opens
 * its cell tooltip that way. At speed (a seek replaying earlier steps) nothing is shown, so nothing
 * is done: a hover changes no state that a later step could depend on.
 */
async function rest(win: Window, action: Targeted<"hover">, move: Move, animate: boolean, timeoutMs: number) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  if (!animate) return true
  if (!(await reveal(win, action.target, el, move, animate))) return false
  lookAt(el, animate)
  const target = clickableIn(el)
  // The tip in the lower right of the control rather than on its middle: the arrow hangs down and to
  // the right of its tip, and aimed at the middle it covered exactly what the hover draws there — the
  // matrix's plus in an empty day, the date of a column heading.
  const box = target.getBoundingClientRect()
  const at = { x: box.left + box.width * 0.72, y: box.top + box.height * 0.78 }
  pointerKey.value = null
  if (!(await approach(at, move))) return false
  hoverOver(target)
  dispatchMouse(win, target, "mouseover", at)
  restPointerOn(target)
  await move.pause(scaled(HOVER_MS))
  return move.current
}

/**
 * A press of the **right** button, which is how the planning board is worked: every span and every
 * row of it answers a right-click with a menu of what can be done to it.
 *
 * The sequence is a real one — the button goes down and up before the menu is asked for — because the
 * board also watches the left button for its selection, and only `button: 2` tells the two apart. No
 * `click()` follows: a right-click is not a click, and the element's own left-click handler must not
 * run. The `contextmenu` event carries the coordinates, which is what the menu opens at.
 */
async function pressRight(win: Window, el: HTMLElement, move: Move, animate: boolean): Promise<boolean> {
  const at = aimInside(win, el)
  if (animate) {
    if (!(await approach(at, move))) return false
    hoverOver(el)
    if (!(await move.pause(scaled(HOVER_MS)))) return false
    pressPointer("right")
  }
  dispatchMouse(win, el, "mousedown", at, { button: 2, buttons: 2 })
  if (animate) await move.pause(scaled(RIGHT_HOLD_MS))
  dispatchMouse(win, el, "mouseup", at, { button: 2, buttons: 0 })
  dispatchMouse(win, el, "contextmenu", at, { button: 2 })
  // The badge stays beside the menu it opened for a moment, before the step lands and the pointer goes.
  if (animate) await move.pause(scaled(RIGHT_SHOW_MS))
  return move.current
}

async function rightClick(
  win: Window,
  action: Targeted<"rightClick">,
  move: Move,
  animate: boolean,
  timeoutMs: number
) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  if (!(await reveal(win, action.target, el, move, animate))) return false

  lookAt(el, animate)
  if (!(await pressRight(win, el, move, animate))) return false
  await settle(move, animate)
  return move.current
}

// ── a map ───────────────────────────────────────────────────────────────────

/**
 * Where a `pinAt` pressed, per target, until its `unpin` takes the pin back. Only a press that really
 * reached the map is kept: an `unpin` with nothing here does nothing, so a scene's exit never moves a
 * pin its step was overtaken before placing.
 */
const pinned = new Map<string, { x: number; y: number }>()

/** Forgets every placed pin without taking it back — for a frame that was reloaded or closed with it. */
export const forgetPins = () => pinned.clear()

/** The Leaflet map inside a target, or the target itself. */
const mapIn = (el: HTMLElement) =>
  el.matches(".leaflet-container") ? el : (el.querySelector<HTMLElement>(".leaflet-container") ?? null)

/**
 * A left click at a point of the map, the way a mouse gives one: down, up, click, all carrying the
 * coordinates, on whatever the map draws there (a tile, usually). Leaflet reads the position off the
 * event, so a bare `el.click()` — which carries none — would place the pin in the map's corner.
 */
async function pressMap(
  win: Window,
  map: HTMLElement,
  fraction: { x: number; y: number },
  move: Move,
  animate: boolean
) {
  const box = map.getBoundingClientRect()
  const at: Point = { x: box.left + box.width * fraction.x, y: box.top + box.height * fraction.y }
  if (animate) {
    if (!(await approach(at, move))) return false
    if (!(await move.pause(scaled(HOVER_MS)))) return false
    pressPointer()
  }
  const el = elementAt(win, at)
  if (!el || !map.contains(el)) return false
  dispatchMouse(win, el, "mousedown", at)
  if (animate) await move.pause(scaled(PRESS_HOLD_MS))
  dispatchMouse(win, el, "mouseup", at)
  // Overtaken while held down: let go without the click, as `pressElement` does.
  if (!move.current) return false
  dispatchMouse(win, el, "click", at)
  return true
}

/**
 * Places the pin with a press on the map, at `at` — fractions of the map's box. What happens next is
 * the app's own: the map sets its marker where it was clicked and pans to centre it.
 */
async function pinAt(win: Window, action: Targeted<"pinAt">, move: Move, animate: boolean, timeoutMs: number) {
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  if (!el || !move.current) return false
  if (!(await reveal(win, action.target, el, move, animate))) return false
  const map = mapIn(el)
  if (!map) return false

  lookAt(el, animate)
  if (!(await pressMap(win, map, action.at, move, animate))) return false
  pinned.set(action.target, action.at)
  await settle(move, animate, MAP_PAN_MS)
  return move.current
}

/**
 * Takes back the pin the last `pinAt` on this target placed, by pressing the point mirrored through
 * the map's centre. The map centres every pin it places, so the old spot sits exactly as far on the
 * other side — the same distance in the map's own projection, which is linear at one zoom level.
 * Nothing to take back is fine.
 */
async function unpin(win: Window, action: Targeted<"unpin">, move: Move, animate: boolean, timeoutMs: number) {
  const placed = pinned.get(action.target)
  if (!placed) return move.current
  const el = await waitForTarget(win, action.target, timeoutMs, move)
  const map = el && mapIn(el)
  if (!map || !move.current) return false
  // A reader moving on straight after the pin was placed catches the map still panning onto it, and
  // the mirrored point is only right once the pin is in the middle. The marker glides with the map.
  const marker = map.querySelector<HTMLElement>(".leaflet-marker-icon")
  if (marker) await waitForStableRect(win, marker)
  if (!(await pressMap(win, map, { x: 1 - placed.x, y: 1 - placed.y }, move, animate))) return false
  // Forgotten only now: an exit overtaken before its press runs again, and has to know there is a pin
  // to take back.
  pinned.delete(action.target)
  await settle(move, animate, MAP_PAN_MS)
  return move.current
}

// ── dragging ────────────────────────────────────────────────────────────────

/** What the frame draws at a point — where a real pointer's events would land. */
const elementAt = (win: Window, at: Point) => (win.document.elementFromPoint(at.x, at.y) as HTMLElement | null) ?? null

/**
 * The part of a target that is actually on screen: its box clipped by the frame and by whatever
 * scroller it sits in. Its own box where nothing clips it.
 *
 * A planning span running over four months is several screens wide, and everything that treats an
 * element as a place — where the pointer aims, what the camera shows — has to mean the part of it the
 * reader can see. `aimForControl` on the whole box put the pointer off the page, the context menu
 * opened where it pressed, and `choose` timed out looking for an option nobody could see.
 */
function visibleBox(win: Window, el: HTMLElement): Rect {
  const box = el.getBoundingClientRect()
  const clip = resolveScroller(win, el)?.getBoundingClientRect()
  const left = Math.max(box.left, 0, clip?.left ?? 0)
  const right = Math.min(box.right, win.innerWidth, clip?.right ?? Infinity)
  const top = Math.max(box.top, 0, clip?.top ?? 0)
  const bottom = Math.min(box.bottom, win.innerHeight, clip?.bottom ?? Infinity)
  if (right <= left || bottom <= top) return { top: box.top, left: box.left, width: box.width, height: box.height }
  return { top, left, width: right - left, height: bottom - top }
}

/** Where the pointer aims on a target: the middle of the part of it that is on screen. */
const aimInside = (win: Window, el: HTMLElement) => aimForControl(visibleBox(win, el))

/** The smallest box holding both — what the camera has to show for a drag to be watchable. */
const union = (a: Rect, b: Rect): Rect => {
  const left = Math.min(a.left, b.left)
  const top = Math.min(a.top, b.top)
  return {
    left,
    top,
    width: Math.max(a.left + a.width, b.left + b.width) - left,
    height: Math.max(a.top + a.height, b.top + b.height) - top,
  }
}

/**
 * The draggable the pointer has hold of, or null when there is nothing there for the browser to carry
 * and the drag is one the page follows with the mouse itself.
 *
 * Decided by what is under the pointer, not by anything the catalog says — the board uses both kinds,
 * sometimes through the same anchor. A day cell of the staff lane is dragged across with the mouse to
 * select days; once days are selected, the very same cell holds a selection block the browser drags.
 * A span is no draggable at all: the board moves and stretches it along its own row by following the
 * mouse, so it is swept like the day cells.
 */
function grabHandle(win: Window, el: HTMLElement, at: Point): HTMLElement | null {
  const under = elementAt(win, at)?.closest<HTMLElement>('[draggable="true"]')
  if (under && (el.contains(under) || under.contains(el))) return under
  if (el.getAttribute("draggable") === "true") return el
  return el.querySelector<HTMLElement>('[draggable="true"]') ?? el.closest<HTMLElement>('[draggable="true"]')
}

/**
 * Walks the pointer from where it is to `to`, calling `step` with the element under it at every
 * position it takes. Animated that is the whole bowed flight; at speed it is the destination alone —
 * both the kinds of drag here care about where the gesture starts and where it ends, not the road.
 */
async function travel(
  win: Window,
  to: Point,
  move: Move,
  animate: boolean,
  step: (_el: HTMLElement, _at: Point) => void
) {
  const visit = (at: Point) => {
    const el = elementAt(win, at)
    if (el) step(el, at)
  }
  if (animate) await flyPointerTo(to, move, visit)
  else visit(to)
  return move.current
}

/**
 * An HTML5 drag: the browser would carry the source itself, so the page hears `dragstart`,
 * `dragover` along the way and `drop` at the end, all sharing one {@link newDataTransfer}. That
 * object is the whole point — the board's drop reads the marked days off it, and the row it is held
 * over previews them.
 */
async function carryDraggable(win: Window, handle: HTMLElement, from: Point, to: Point, move: Move, animate: boolean) {
  try {
    return await carryDraggableHeld(win, handle, from, to, move, animate)
  } finally {
    pointerHolding.value = false
    pointerCarry.value = null
  }
}

/**
 * The drag itself, with the pointer shown holding what it picked up. A dispatched drag gets no ghost
 * from the browser, so the element never seemed to move — only the arrow did. The box it carries is
 * the picked-up element's own size, grabbed where the pointer took hold of it.
 */
async function carryDraggableHeld(
  win: Window,
  handle: HTMLElement,
  from: Point,
  to: Point,
  move: Move,
  animate: boolean
) {
  const data = newDataTransfer(win)
  dispatchMouse(win, handle, "mousedown", from, { buttons: 1 })
  dispatchDrag(win, handle, "dragstart", from, data)
  if (animate) {
    const box = visibleBox(win, handle)
    pointerHolding.value = true
    pointerCarry.value = { dx: box.left - from.x, dy: box.top - from.y, width: box.width, height: box.height }
    // A beat with the thing lifted, before it travels: picking up is part of the gesture.
    await move.pause(scaled(LIFT_MS))
  }

  let over: HTMLElement | null = null
  const cross = (el: HTMLElement, at: Point) => {
    if (el !== over) {
      if (over) dispatchDrag(win, over, "dragleave", at, data)
      dispatchDrag(win, el, "dragenter", at, data)
      over = el
    }
    dispatchDrag(win, el, "dragover", at, data)
  }
  if (!(await travel(win, to, move, animate, cross))) {
    // Overtaken mid-flight: the gesture still has to end, or the board stays in the middle of a drag —
    // its block draggable, its selection held — for every step after. No drop: nothing is moved.
    dispatchDrag(win, handle.isConnected ? handle : (over ?? handle), "dragend", to, data)
    return false
  }

  await move.pause(Math.max(animate ? scaled(HOVER_MS) : 0, DRAG_HOLD_MS))
  // Aimed again at what is under the pointer *now* — see `sweepWithMouse`.
  const landed = elementAt(win, to) ?? over ?? handle
  if (landed !== over) cross(landed, to)
  dispatchDrag(win, landed, "drop", to, data)
  // The source may have been redrawn away while the pointer carried it; `dragend` is what tells the
  // page the gesture is over — the board clears its selection there — so it must land on something
  // that is still in the document.
  dispatchDrag(win, handle.isConnected ? handle : landed, "dragend", to, data)
  return move.current
}

/**
 * A drag the page follows itself: the button goes down, the pointer crosses elements — each one told
 * it was entered and left, as a mouse does — and the button comes up where it stopped. The board's
 * day selection is built this way, out of `mouseover` on the cells the pointer passes, and a span is
 * moved or stretched this way, following the `mousemove`s along its own row.
 */
async function sweepWithMouse(win: Window, source: HTMLElement, from: Point, to: Point, move: Move, animate: boolean) {
  // The button is held across the whole sweep; the board draws the selection itself, so nothing is
  // carried — only the held ring shows.
  if (animate) pointerHolding.value = true
  try {
    return await sweepHeld(win, source, from, to, move, animate)
  } finally {
    pointerHolding.value = false
  }
}

async function sweepHeld(win: Window, source: HTMLElement, from: Point, to: Point, move: Move, animate: boolean) {
  let over: HTMLElement | null = elementAt(win, from) ?? source
  dispatchMouse(win, over, "mousedown", from, { buttons: 1 })

  const cross = (el: HTMLElement, at: Point) => {
    if (el !== over) {
      if (over) dispatchMouse(win, over, "mouseout", at, { buttons: 1, relatedTarget: el })
      dispatchMouse(win, el, "mouseover", at, { buttons: 1, relatedTarget: over })
      over = el
    }
    dispatchMouse(win, el, "mousemove", at, { buttons: 1 })
  }
  if (!(await travel(win, to, move, animate, cross))) {
    // Overtaken mid-sweep: the button still comes up, or the board keeps its button held down and the
    // next drag or right-click extends a selection nobody is making.
    dispatchMouse(win, over && over.isConnected ? over : source, "mouseup", to, { buttons: 0 })
    return false
  }

  /*
   * The page redraws under a pointer that is dragging — the gantt rebuilds the very cells it is
   * selecting, a beat behind — so the node the travel last touched can already be gone from the
   * document, and an event on a detached node reaches nobody. The arrival is therefore aimed again at
   * whatever is under the pointer now, and only then released. Without it a selection came out one day
   * short and the release was heard by nobody, which left the days selected but not something the
   * browser would pick up: the next drag had nothing to carry.
   */
  await move.pause(DRAG_HOLD_MS)
  const landed = elementAt(win, to) ?? over ?? source
  cross(landed, to)

  // The beat before letting go is not only for the look of it — see DRAG_HOLD_MS.
  await move.pause(Math.max(animate ? scaled(HOVER_MS) : 0, DRAG_HOLD_MS))
  dispatchMouse(win, elementAt(win, to) ?? landed, "mouseup", to, { buttons: 0 })
  return move.current
}

/**
 * Drags one region onto another. Both ends have to be on screen **at the same time**: a hand holding
 * something cannot scroll the page under it, so a drag whose destination is off screen is refused
 * rather than faked.
 */
async function drag(win: Window, action: Targeted<"drag">, move: Move, animate: boolean, timeoutMs: number) {
  const source = await waitForTarget(win, action.from, timeoutMs, move)
  const destination = await waitForTarget(win, action.to, timeoutMs, move)
  if (!source || !destination || !move.current) return false
  if (!(await reveal(win, action.from, source, move, animate))) return false
  if (!(await reveal(win, action.to, destination, move, animate))) return false
  // Revealing the destination may have scrolled the source back out again.
  if (!isAnchorVisible(win, source)) {
    if (import.meta.env.DEV)
      console.warn(`[view-help] drag refused: "${action.from}" and "${action.to}" are not on screen together`)
    return false
  }

  const seenSource = visibleBox(win, source)
  const seenDestination = visibleBox(win, destination)
  const from = aimForControl(seenSource)
  const to = aimForControl(seenDestination)
  lookAtRect(union(seenSource, seenDestination), animate)
  if (animate) {
    if (!(await approach(from, move))) return false
    hoverOver(source)
    if (!(await move.pause(scaled(HOVER_MS)))) return false
    pressPointer()
  }

  const handle = grabHandle(win, source, from)
  const done = handle
    ? await carryDraggable(win, handle, from, to, move, animate)
    : await sweepWithMouse(win, source, from, to, move, animate)
  if (!done) return false
  await settle(move, animate)
  return move.current
}

/** Runs one action. False when its target never appeared, or the move was overtaken. */
async function runAction(action: ViewHelpAction, move: Move, animate: boolean, timeoutMs: number): Promise<boolean> {
  const win = frameWindow.value
  if (!win) return false

  switch (action.do) {
    case "wait":
      if (animate) await move.pause(scaled(action.ms))
      return move.current
    case "hover":
      return rest(win, action, move, animate, timeoutMs)
    case "escape":
      pressEscape(win)
      await settle(move, animate)
      releaseFieldFocus(win)
      return move.current
    case "choose":
      return choose(win, action, move, animate, timeoutMs)
    case "rightClick":
      return rightClick(win, action, move, animate, timeoutMs)
    case "drag":
      return drag(win, action, move, animate, timeoutMs)
    case "clear":
      return clear(win, action, move, animate)
    case "cancel":
      return cancel(win, action, move, animate, timeoutMs)
    case "scroll":
      return scrollPage(win, action, move, animate, timeoutMs)
    case "openTab":
      return openTab(win, action, move, animate, timeoutMs)
    case "showToday":
      return showToday(win, action, move, animate, timeoutMs)
    case "expand":
    case "collapse":
      return disclose(win, action, move, animate, timeoutMs)
    case "check":
    case "uncheck":
      return switchTo(win, action, move, animate, timeoutMs)
    case "pinAt":
      return pinAt(win, action, move, animate, timeoutMs)
    case "unpin":
      return unpin(win, action, move, animate, timeoutMs)
    default:
      return useControl(win, action, move, animate, timeoutMs)
  }
}

/** Runs actions in order, stopping at the first that fails. */
/**
 * @param timeoutMs how long each target may take to appear. The usual budget is for a dialog opening;
 *   a way to another view waits for a whole page — see `walkTheWay`.
 */
export async function runActions(
  actions: ViewHelpAction[],
  move: Move,
  animate: boolean,
  timeoutMs = ACTION_TIMEOUT_MS
): Promise<boolean> {
  for (const action of actions) {
    if (!(await runAction(action, move, animate, timeoutMs))) return false
  }
  return true
}
